import * as React from 'react';
import { connect } from 'react-redux';
import { getParameterByName, SkipToMainContentButton, Spinner } from '../../../../Shared';
import {
  goToNextSlide,
  goToPreviousSlide,
  updateCurrentSlide,
} from '../../../actions/classroomSessions';
import {
  ClassroomLesson
} from '../../../interfaces/classroomLessons';
import * as CustomizeIntf from '../../../interfaces/customize';
import {
  ClassroomLessonSession,
  ClassroomUnitId,
  QuestionSubmissionsList
} from '../interfaces';
import CLStudentFillInTheBlank from '../play/fillInTheBlank';
import CLStudentListBlanks from '../play/listBlanks';
import CLStudentLobby from '../play/lobby';
import CLStudentModelQuestion from '../play/modelQuestion';
import CLStudentMultistep from '../play/multistep';
import CLStudentSingleAnswer from '../play/singleAnswer';
import CLStudentStatic from '../play/static';
import { Navbar as StudentLessonsNavbar } from '../../navbar/studentLessonsNavbar';

const MAIN_CONTENT_ID = 'lessons-main-content'

interface ReducerSidebarProps extends React.Props<any> {
  classroomSessions: any,
  classroomLesson: any,
  customize: any,
}

interface PassedSidebarProps extends React.Props<any> {
  match: any,
  classroomSessionId?: string,
  isOpen?: boolean,
  onClose?: () => void
}

class Sidebar extends React.Component<ReducerSidebarProps & PassedSidebarProps & DispatchFromProps, any> {

  constructor(props) {
    super(props);

    const classroomUnitId: ClassroomUnitId|null = getParameterByName('classroom_unit_id')
    this.state = {
      classroomUnitId,
      currentSlide: null
    }
  }

  componentDidUpdate(prevProps) {
    const { classroomSessions } = this.props
    const { current_slide } = classroomSessions.data
    const { currentSlide } = this.state
    if (current_slide !== prevProps.classroomSessions.data.current_slide) {
      this.scrollToSlide(current_slide)
    }
    if (!currentSlide) {
      this.scrollToSlide(current_slide)
    }
  }

  scrollToSlide(slide_id: string) {
    const el = document.getElementById(slide_id)
    const sidebar = document.getElementsByClassName("side-bar")[0];
    if (el && sidebar) {
      this.setState({
        currentSlide: el,
      })
      this.scrollToPosition(sidebar, el.offsetTop - 65, 0)
    }
  }

  // borrowed from https://stackoverflow.com/questions/12102118/scrollintoview-animation/32484034
  scrollToPosition(elem, pos, count) {
    if (count > 15) {
      return;
    }
    let y = elem.scrollTop;
    y += Math.round( ( pos - y ) * 0.3 );
    if (Math.abs(y-pos) <= 2) {
      elem.scrollTop = pos;
      return;
    }
    elem.scrollTop = y;
    setTimeout(() => {this.scrollToPosition(elem, this.state.currentSlide.offsetTop - 105, count+1)}, 40);
  }

  goToSlide = (slide_id: string) => {
    const { dispatch, classroomSessionId, } = this.props
    if (classroomSessionId) {
      dispatch(updateCurrentSlide(slide_id, classroomSessionId));
    }
  }

  goToNext = () => {
    const { dispatch, classroomSessionId, classroomSessions, customize, } = this.props
    const action = goToNextSlide(classroomSessions.data, customize.editionQuestions, classroomSessionId)
    if (action) { dispatch(action) }
  }

  goToPrevious = () => {
    const { dispatch, classroomSessionId, classroomSessions, customize, } = this.props
    const action = goToPreviousSlide(classroomSessions.data, customize.editionQuestions, classroomSessionId)
    if (action) { dispatch(action) }
  }

  renderFooter(slides: string[], currentSlide: string) {
    const currentIndex = slides.indexOf(String(currentSlide))
    const isFirst = currentIndex <= 0
    const isLast = currentIndex === slides.length - 1
    const currentLabel = Number(currentSlide) === 0 ? 'Title Slide' : `Slide ${currentSlide}`

    return (
      <div className="sidebar-footer">
        <button className="sidebar-footer-previous focus-on-light" disabled={isFirst} onClick={this.goToPrevious} type="button">Previous</button>
        <span className="sidebar-footer-label">{currentLabel}</span>
        <button className="sidebar-footer-next focus-on-light" disabled={isLast} onClick={this.goToNext} type="button">Next</button>
      </div>
    )
  }

  getSlideLabel(slide: string, length: number, title: string | undefined, isLobbySlide: boolean): string {
    if (isLobbySlide) {
      return title ? `Title Slide - ${title}` : 'Title Slide';
    }
    return title ? `Slide ${slide} of ${length} - ${title}` : `Slide ${slide} of ${length}`;
  }

  render() {
    const { data, hasreceiveddata, }: { data: ClassroomLessonSession, hasreceiveddata: boolean } = this.props.classroomSessions;
    const lessonData: ClassroomLesson = this.props.classroomLesson.data;
    const lessonDataLoaded: boolean = this.props.classroomLesson.hasreceiveddata;
    const editionData: CustomizeIntf.EditionQuestions = this.props.customize.editionQuestions;
    if (hasreceiveddata && data && lessonDataLoaded) {
      const questions = editionData.questions;
      const length = questions ? Number(questions.length) - 1 : 0;
      const currentSlide = data.current_slide;
      const components: JSX.Element[] = [];
      let counter = 0;
      for (const slide in questions) {
        counter += 1;
        const isActive = currentSlide === slide;
        const activeClass = isActive ? 'active' : '';
        let thumb;
        let title = editionData.questions[slide].data.teach.title
        let prompt = data.prompts && data.prompts[slide] ? data.prompts[slide] : null;
        let model: string|null = data.models && data.models[slide] ? data.models[slide] : null;
        let mode: string | null = data.modes && data.modes[slide] ? data.modes[slide] : null;
        let submissions: QuestionSubmissionsList | null = data.submissions && data.submissions[slide] ? data.submissions[slide] : null;
        let selected_submissions = data.selected_submissions && data.selected_submissions[slide] ? data.selected_submissions[slide] : null;
        let selected_submission_order: Array<string>|null = data.selected_submission_order && data.selected_submission_order[slide] ? data.selected_submission_order[slide] : null;
        let props = { mode, submissions, selected_submissions, selected_submission_order};
        switch (questions[slide].type) {
          case 'CL-LB':
            thumb = (
              <CLStudentLobby data={data} projector={true} title={lessonData.title} />
            );
            break;
          case 'CL-ST':
            thumb = (
              <CLStudentStatic data={questions[slide].data} />
            );
            break;
          case 'CL-MD':
            thumb = (
              <CLStudentModelQuestion data={questions[slide].data} model={model} prompt={prompt} />
            );
            break;
          case 'CL-SA':
            thumb = (
              <CLStudentSingleAnswer data={questions[slide].data} handleStudentSubmission={() => {}} {...props} />
            );
            break
          case 'CL-FB':
            thumb = (
              <CLStudentFillInTheBlank data={questions[slide].data} handleStudentSubmission={() => {}} {...props} />
            );
            break;
          case 'CL-FL':
            thumb = (
              <CLStudentListBlanks data={questions[slide].data} handleStudentSubmission={() => {}} {...props} />
            );
            break;
          case 'CL-EX':
            thumb = (
              <CLStudentStatic data={questions[slide].data} />
            );
            break;
          case 'CL-MS':
            thumb = (
              <CLStudentMultistep data={questions[slide].data} handleStudentSubmission={() => {}} {...props} />
            )
            break;
          default:
            thumb = questions[slide].type;
        }
        const isLobbySlide = Number(slide) === 0
        const headerText = isLobbySlide ? 'Title Slide' : `Slide ${slide}`
        const counterText = isLobbySlide ? '' : `${slide} of ${length}`
        const ariaLabel = this.getSlideLabel(slide, length, title, isLobbySlide);
        components.push((
          <button
            aria-current={isActive ? 'true' : undefined}
            aria-label={ariaLabel}
            className="interactive-wrapper focus-on-light"
            id={slide}
            key={counter}
            onClick={() => this.goToSlide(slide)}
            type="button"
          >
            <div aria-hidden="true" className="sidebar-header">
              <p className={`slide-number ${activeClass}`}>{headerText}</p>
            </div>
            <div aria-hidden="true" className={`slide-preview ${activeClass}`} tabIndex={-1}>
              <div className="scaler" inert="">
                <StudentLessonsNavbar counterText={counterText} />
                {thumb}
              </div>
            </div>
          </button>
        ));
      }
      const { isOpen, onClose } = this.props;
      const sidebarClassName = isOpen ? 'side-bar open' : 'side-bar';

      return (
        <aside aria-label="Lesson slides navigation" className={sidebarClassName}>
          <div className="sidebar-mobile-header">
            <span className="sidebar-title">Slides</span>
            <button
              aria-label="Close sidebar"
              className="close-sidebar-button focus-on-light"
              onClick={onClose}
              type="button"
            >
              <i className="fa fa-close" />
            </button>
          </div>
          <SkipToMainContentButton mainContentId={MAIN_CONTENT_ID} />
          <nav aria-label="Slides">
            {components}
          </nav>
          {this.renderFooter(questions ? Object.keys(questions) : [], currentSlide)}
        </aside>
      );
    }
    const { isOpen } = this.props;
    const sidebarClassName = isOpen ? 'side-bar open' : 'side-bar';

    return (
      <aside aria-label="Lesson slides navigation" className={sidebarClassName}>
        <div className="sidebar-loading">
          <Spinner />
        </div>
      </aside>
    );
  }

}

function select(props) {
  return {
    classroomSessions: props.classroomSessions,
    classroomLesson: props.classroomLesson,
    customize: props.customize
  };
}

function mergeProps(stateProps: Object, dispatchProps: Object, ownProps: Object) {
  return {...ownProps, ...stateProps, ...dispatchProps}
}

export interface DispatchFromProps {
  dispatch: any;
}

export default connect<ReducerSidebarProps, DispatchFromProps, PassedSidebarProps>(select, dispatch => ({dispatch}))(Sidebar);
